/** Shared with the stories: two definitions of a tab stop agree by luck, then stop. */
export const TAB_STOP =
	"a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

/** Controls that take the panel field. Dismissing on one releases the field before `takePanel`
    reads it, so the hand-over stops racing and plays as a close and an unrelated open. */
export const PANEL_CONTROL =
	"[data-fa-menu-toggle], [data-fa-brand-switcher-toggle], [data-fa-nav-menu-trigger]";

/** Tab stops in `scope`, in document order, minus the hidden and inert ones. */
export const tabStops = (scope: ParentNode): HTMLElement[] =>
	[...scope.querySelectorAll<HTMLElement>(TAB_STOP)].filter(
		(element) =>
			element.closest("[inert]") === null &&
			element.checkVisibility({ checkVisibilityCSS: true }),
	);

/** Call after clearing `inert`: an inert subtree cannot take focus, so the reverse order
    silently does nothing. */
export const focusFirstStop = (panel: HTMLElement): void => {
	tabStops(panel)[0]?.focus({ preventScroll: true });
};

export const takesPanel = (node: EventTarget | null): boolean =>
	node instanceof Element && node.closest(PANEL_CONTROL) !== null;

/** Whether `root` holds focus — remembered, not asked. At a media-query crossing the new layout
    has already hidden what was focused and the browser has already blurred it, so asking then
    answers `<body>` however the user got there. */
export const trackFocusWithin = (root: HTMLElement, signal: AbortSignal): (() => boolean) => {
	let within = root.contains(document.activeElement);

	root.addEventListener("focusin", () => (within = true), { signal });

	root.addEventListener(
		"focusout",
		(event) => {
			/* A blur to nothing is the layout pulling the element out from under focus, not the
			   user leaving: only a move to a real element outside gives the claim up. */
			if (event.relatedTarget instanceof Node && !root.contains(event.relatedTarget))
				within = false;
		},
		{ signal },
	);

	return () => within;
};
